/**
 * MCP-UI-1 — STDIO server form logic (extracted so it's unit-testable
 * outside the big AdminView shell).
 *
 * Backend (mcp_client._build_bifrost_payload) maps a STDIO server to Bifrost:
 *   command = command || "npx"
 *   args    = args || (package ? ["-y", package] : [])
 *   envs    = keys(config)            // env-var NAMES; values via credential
 *
 * So the four runtimes map as:
 *   npx    → send `package` (Bifrost builds `npx -y <package>`)
 *   uvx    → send `package` (for display/metadata + the backend's required-
 *            field check) AND command "uvx" with `args` = exactly what the
 *            admin typed — nothing auto-prepended (see uvx branch below).
 *   docker → command "docker", args = [...user args, e.g. run --rm -i <image>]
 *   custom → command <binary>, args = [...user args]
 */

export const STDIO_RUNTIME_COMMAND = {
  npx: "npx",
  uvx: "uvx",
  docker: "docker",
  custom: "",
};

/** Parse a textarea of args — one argument per line, trimmed, blanks dropped. */
export function parseArgsText(text) {
  if (!text) return [];
  return String(text)
    .split("\n")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Build the {package?, command?, args?} additions for a STDIO register payload
 * from the form state. Returns only the keys that apply to the chosen runtime.
 */
export function buildStdioServerFields(form) {
  const runtime = form.stdio_runtime || "npx";
  const extra = parseArgsText(form.args_text);
  if (runtime === "npx") {
    const out = {};
    if (form.package) out.package = form.package;
    return out;
  }
  if (runtime === "uvx") {
    // Bug fix (2026-10-16): this used to be `args: [form.package, ...extra]`
    // — the Python package was ALWAYS forced into args[0], with the admin's
    // own "Arguments" lines appended AFTER it. That's wrong for a real class
    // of Python MCP servers, not just one package: `uv`/`uvx` reads flags
    // like `--with "<constraint>"` as applying to WHATEVER COMES NEXT, so
    // any such flag has to come BEFORE the package name on the command line
    // to have any effect (e.g. `uvx --with "mcp<2" postgres-mcp==0.3.0 ...`
    // — pinning a package's own transitive dependency because its own
    // release has no upper bound and silently resolves a breaking SDK
    // version otherwise). With the package hardcoded to position 0, an
    // admin could never express that ordering through this form — and the
    // top-level `package` field (which the backend requires and the server
    // list displays) was never populated for this runtime at all, so EVERY
    // uvx registration 400'd with "STDIO connection requires 'package'
    // field" regardless of what was typed. Confirmed live against a real
    // tenant before this fix.
    //
    // It's not just an ordering convenience, either — some of these flags
    // are safety-critical and must not be silently dropped. Live-verified
    // 2026-10-16: a postgres-mcp server registered with NO `--access-mode`
    // flag at all defaults to unrestricted (write-capable, including
    // DELETE) rather than failing closed to read-only. A form design that
    // only lets the admin append flags AFTER an auto-inserted package name
    // can't guarantee a trailing safety flag survives either — there's no
    // single safe default position to auto-insert the package at.
    //
    // Fix: set `package` properly (same as the npx branch below), and let
    // `args` be exactly what's in the Arguments textarea — same freedom the
    // `docker`/`custom` runtimes already have. The admin places the package
    // name wherever it needs to go in their own argument list, so every
    // flag — before or after the package — is under explicit control and
    // nothing safety-relevant can be silently dropped by this form.
    return { command: "uvx", package: form.package, args: extra };
  }
  if (runtime === "docker") {
    return { command: "docker", args: extra };
  }
  // custom
  return { command: (form.command || "").trim(), args: extra };
}

/**
 * True when the form has enough to register a STDIO server for its runtime —
 * npx needs a package; uvx needs a package AND at least one Arguments line
 * (since args — not package — is the actual spawned invocation); docker/
 * custom need at least one arg / a command respectively.
 */
export function stdioReady(form) {
  const runtime = form.stdio_runtime || "npx";
  if (runtime === "npx") return Boolean(form.package);
  // uvx: `package` alone isn't enough any more (see the uvx branch of
  // buildStdioServerFields above) — the admin must ALSO type the actual
  // invocation (which includes the package name itself, in whatever
  // position it belongs) into the Arguments box, or nothing gets spawned.
  if (runtime === "uvx") return Boolean(form.package) && parseArgsText(form.args_text).length > 0;
  if (runtime === "docker") return parseArgsText(form.args_text).length > 0;
  return Boolean((form.command || "").trim()); // custom
}

/**
 * Produce the mcpForm patch for a preset (from GET /api/mcp/stdio-presets).
 * Carries the suggested env-var keys + credential provider so the UI can
 * point the admin at creating the matching generic_env credential.
 */
export function applyStdioPreset(preset) {
  if (!preset) return {};
  return {
    connection_type: "stdio",
    stdio_runtime: preset.runtime || "npx",
    package: preset.package || "",
    command: "",
    args_text: (preset.args || []).join("\n"),
    presetEnvKeys: preset.env_keys || [],
    presetCredentialProvider: preset.credential_provider || null,
    presetHint: preset.hint || "",
  };
}
